import type { FormEvent } from "react";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Code, DollarSign, Mail, Server } from "lucide-react";

import { Button } from "@/components/ui/button";

import { siteConfig } from "../config/site";

type FeedbackState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

export function AboutPage() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [feedbackState, setFeedbackState] = useState<FeedbackState>({ kind: "idle" });

  const canSubmit = useMemo(() => message.trim().length >= 10, [message]);

  async function submitFeedback(event: FormEvent) {
    event.preventDefault();

    if (feedbackState.kind === "submitting") {
      return;
    }

    if (!canSubmit) {
      setFeedbackState({ kind: "error", message: "Please write a longer message." });
      return;
    }

    setFeedbackState({ kind: "submitting" });

    try {
      const response = await fetch("/v1/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim() ? email.trim() : undefined,
          message: message.trim(),
          page: window.location.pathname,
          honeypot: company,
        }),
      });

      if (!response.ok) {
        const raw = await response.text();
        throw new Error(raw || `request failed: ${response.status}`);
      }

      setEmail("");
      setMessage("");
      setCompany("");
      setFeedbackState({ kind: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Request failed";
      setFeedbackState({ kind: "error", message });
    }
  }

  return (
    <>
      <Helmet>
        <title>About · {siteConfig.name}</title>
        <meta name="description" content={`About ${siteConfig.name}.`} />
      </Helmet>

      <section className="pb-16">
        <h1 className="font-display text-4xl tracking-tight sm:text-5xl">About</h1>
        <p className="mt-4 max-w-xl text-muted-foreground">
          Password managers shouldn&apos;t cost $36/year. They&apos;re table stakes for security—not
          a premium product. key.store is open source and free forever.
        </p>
      </section>

      <section className="border-t border-border/60 py-16">
        <div className="grid gap-10 sm:grid-cols-3">
          <div className="flex gap-4">
            <Code className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium">Open source</p>
              <div className="mt-3 space-y-2 text-sm">
                <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground" href={siteConfig.links.repo} target="_blank" rel="noreferrer">
                  Source code <ArrowRight className="h-3 w-3" />
                </a>
                <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground" href={siteConfig.links.issues} target="_blank" rel="noreferrer">
                  Roadmap <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <Server className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium">Infrastructure</p>
              <p className="mt-1 text-sm text-muted-foreground">
                We run our own servers. Monitoring available to contributors.
              </p>
              <div className="mt-3 space-y-2 text-sm">
                <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground" href={siteConfig.links.graphs} target="_blank" rel="noreferrer">
                  System metrics <ArrowRight className="h-3 w-3" />
                </a>
                <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground" href={siteConfig.links.traces} target="_blank" rel="noreferrer">
                  Request traces <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <DollarSign className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-medium">Finances</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Public ledger of income and expenses.
              </p>
              <div className="mt-3 text-sm">
                <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground" href={siteConfig.links.ledger} target="_blank" rel="noreferrer">
                  View ledger <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border/60 py-16">
        <div className="flex gap-4">
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div>
            <p className="font-medium">Contact</p>
            <div className="mt-3 space-y-2 text-sm text-muted-foreground">
              <p>
                Privacy questions:{" "}
                <a href={`mailto:${siteConfig.privacyEmail}`} className="font-mono hover:text-foreground">
                  {siteConfig.privacyEmail}
                </a>
              </p>
              <p>
                DMCA notices:{" "}
                <a href={`mailto:${siteConfig.dmcaEmail}`} className="font-mono hover:text-foreground">
                  {siteConfig.dmcaEmail}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="feedback" className="border-t border-border/60 py-16">
        <h2 className="font-display text-2xl tracking-tight sm:text-3xl">Feedback</h2>
        <p className="mt-3 max-w-lg text-muted-foreground">
          Found a bug? Missing a feature? We read everything.
        </p>

        <form onSubmit={submitFeedback} className="mt-8 max-w-lg space-y-4">
          <label className="block space-y-2">
            <span className="text-sm text-muted-foreground">Your email (optional)</span>
            <input
              className="w-full rounded-xl border border-border/60 bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-foreground/30"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>

          <label className="hidden">
            <span>Company</span>
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm text-muted-foreground">Message</span>
            <textarea
              className="min-h-32 w-full resize-y rounded-xl border border-border/60 bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-foreground/30"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What's on your mind?"
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" className="rounded-full px-6" disabled={!canSubmit || feedbackState.kind === "submitting"}>
              {feedbackState.kind === "submitting" ? "Sending…" : "Send"}
            </Button>
            {feedbackState.kind === "success" && (
              <p className="text-sm text-muted-foreground">Sent. Thanks!</p>
            )}
            {feedbackState.kind === "error" && (
              <p className="text-sm text-destructive">{feedbackState.message}</p>
            )}
          </div>
        </form>
      </section>
    </>
  );
}
